/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useMemo } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import {
  Breadcrumbs,
  Typography,
  FormControl,
  InputAdornment,
  IconButton,
  Select,
  Grid,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FilterContainer } from "../../components/filter_container";
import {
  viewTypographyStyles,
  StyledBreadCrumbContainer,
  StyledLink,
  StyledInputLabel,
  selectBaseStyles
} from "../audit_logs/styles";
import { DiscountLogPageContainer, ActionContainer } from "./styles";
import ClearIcon from '@mui/icons-material/Clear';
import { SelectedMenuItem } from "../../components/selected_menu_item";
import { capitalize } from 'lodash'
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import { useInfiniteQuery, useQuery, keepPreviousData } from "@tanstack/react-query";
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { ResetButton } from "../../components/reset_button";
import { UpdateButton } from "../../components/update_button";
import DropdownSearchBar from "../../components/dorpdown_searchbar";
import { MenuHeading } from "../../components/menu_heading";
import { Loader } from "../../components/loader";
import camelize from "../../utils/camelize";
import { IndexTable } from "../../components/index_table";
import { formatDate } from "../../utils/formatDate";
import { StyledMenuItem } from "../../components/styled_menu_item";
import { datePickerStyles } from "../audit_logs/styles";
import { buildQueryParams } from "../../utils/buildQueryParams";
import { useNotificationContext } from "../../contexts/notificationContext";
import dayjs from "dayjs";
import { Query_Keys } from "../../lib/queryKeys";
import { PerPage } from "../../constants/perPage";
import { 
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  normalizeEmployee,
  normalizeRestaurantGroup
} from "../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { LogAttributes, LogValueGroups } from "./log_display";
import { isValidDayjsValue } from "../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../utils/isValidCustomDateRange";
import { CARD_SOURCE_ALL, CardSourceFilter, cardSourceParams } from "../../components/card_source_filter";

export const DiscountCardLogPage = () => {
  const singleRestaurantGroupAutoApplied = React.useRef(false)
  const singleDiscountCardAutoApplied = React.useRef(false)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [adminSearch, setAdminSearch] = useState('')
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [discountCardSearch, setDiscountCardSearch] = useState("")
  const [csvLoading, setCsvLoading] = useState(false)
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [discountCardOptionsCache, setDiscountCardOptionsCache] = useState([])
  const [adminUserOptionsCache, setAdminUserOptionsCache] = useState([])
  const showNotification = useNotificationContext();
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedDiscountCardSearch = useDebounce(discountCardSearch.trim(), 300)
  const debouncedAdminSearch = useDebounce(adminSearch.trim(), 300)

  const actionType = [
    {label: 'Create', value: 'create'},
    {label: 'Update', value: 'update'},
    {label: 'Destroy', value: 'destroy'}
  ]

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      }
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchDiscountCardsList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/discount_card_log_card", {
      params: {
        restaurant_group_id: filters?.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedDiscountCardSearch || undefined,
      }
    });
    return response?.data || [];
  };

  const {
    data: discountCards = [],
    isFetching: discountCardFetching,
    isFetchingNextPage: discountCardsFetchingNextPage,
    fetchNextPage: fetchNextDiscountCardsPage,
    hasNextPage: hasNextDiscountCardsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.discount_card_log_cards({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedDiscountCardSearch,
    }),
    queryFn: fetchDiscountCardsList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchAdminUsers = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/admin_app_users_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedAdminSearch || undefined,
      }
    })
    return response?.data?.map(normalizeEmployee) || []
  }

  const {
    data: adminUsers = [],
    isFetching: adminUserFetching,
    isFetchingNextPage: adminUsersFetchingNextPage,
    fetchNextPage: fetchNextAdminUsersPage,
    hasNextPage: hasNextAdminUsersPage,
  } = useInfiniteQuery({
    queryKey: [...Query_Keys.admin_user_dropdown, { search: debouncedAdminSearch }],
    queryFn: fetchAdminUsers,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: discountCardMenuRef, targetRef: discountCardLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextDiscountCardsPage,
    hasNextPage: hasNextDiscountCardsPage,
    isFetchingNextPage: discountCardsFetchingNextPage,
  })

  const { rootRef: adminUserMenuRef, targetRef: adminUserLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextAdminUsersPage,
    hasNextPage: hasNextAdminUsersPage,
    isFetchingNextPage: adminUsersFetchingNextPage,
  })

  const fetchDiscountCardLogs = async () => {
    const queryParameters = formattedFilters(appliedFilters)
    const response = await api.get('/v1/admin/discount_cards/discount_card_logs', {
      params: {
        per_page: itemsPerPage,
        page: page,
        ...queryParameters,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      }
    })
    const result = {logData: response?.data, pageCount: response?.headers["total-count"]}
    return result
  }

  const { data, isFetching } = useQuery({
    queryKey: Query_Keys.discount_card_logs({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchDiscountCardLogs,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurantGroups, 'label', ['value']))
  }, [restaurantGroups])

  useEffect(() => {
    setDiscountCardOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, discountCards, 'value', ['label']))
  }, [discountCards])

  useEffect(() => {
    setAdminUserOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, adminUsers, 'value', ['label']))
  }, [adminUsers])

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && restaurantGroups?.length === 1
  const hasSingleAvailableDiscountCard = !debouncedDiscountCardSearch && discountCards?.length === 1

  useEffect(() => {
    if (filters?.restaurant_group_id || singleRestaurantGroupAutoApplied.current || !hasSingleAvailableRestaurantGroup) {
      return
    }

    singleRestaurantGroupAutoApplied.current = true
    setFilters(prev => ({...prev, restaurant_group_id: restaurantGroups?.[0]?.label}))
  }, [filters?.restaurant_group_id, hasSingleAvailableRestaurantGroup, restaurantGroups])

  useEffect(() => {
    if (filters?.discount_card_name || singleDiscountCardAutoApplied.current || !hasSingleAvailableDiscountCard) {
      return
    }

    singleDiscountCardAutoApplied.current = true
    setFilters(prev => ({...prev, discount_card_name: discountCards?.[0]?.value}))
  }, [discountCards, filters?.discount_card_name, hasSingleAvailableDiscountCard])

  const handleStartDateChange = (date) => {
    const isValidStartDate = isValidDayjsValue(date);
    const nextEndDate = !date
      ? null
      : (isValidStartDate && !isValidDayjsValue(endDate) ? dayjs().startOf("day") : endDate);

    setStartDate(date);
    setEndDate(nextEndDate);
    setFilters((prev) => ({
      ...prev,
      from_date: isValidStartDate ? `${date.date()}/${date.month() + 1}/${date.year()}` : null,
      to_date: isValidDayjsValue(nextEndDate)
        ? `${nextEndDate.date()}/${nextEndDate.month() + 1}/${nextEndDate.year()}`
        : null,
    }));
    setFiltersChanged(true)
  }

  const handleToDateChange = (date) => {
    setEndDate(date);
    setFilters((prev) => ({
      ...prev,
      to_date: isValidDayjsValue(date) ? `${date.date()}/${date.month() + 1}/${date.year()}` : null,
    }));
    setFiltersChanged(true)
  }

  const handleEventTypeChange = (event) => {
    setFilters({...filters, action_type: event.target.value})
    setFiltersChanged(true)
  }

  const handleActionTypeClear = () => {
    setFilters({...filters, action_type: null})
    setFiltersChanged(true)
  }

  const handleAdminUserChange = (event) => {
    setFilters({...filters, performed_by_user_id: event.target.value})
    setFiltersChanged(true)
  }

  const handleRestaurantGroupChange = (event) => {
    setFilters({...filters, restaurant_group_id: event.target.value, discount_card_name: ''})
    setFiltersChanged(true)
  }

  const handleDiscountCardChange = (event) => {
    setFilters({...filters, discount_card_name: event.target.value})
    setFiltersChanged(true)
  }

  const handleAdminUserClear = () => {
    setFilters({...filters, performed_by_user_id: null})
    setFiltersChanged(true)
  }

  const handleRestaurantGroupClear = () => {
    setFilters({...filters, restaurant_group_id: null, card_source: CARD_SOURCE_ALL})
    setFiltersChanged(true)
  }

  const handleDiscountCardClear = () => {
    setFilters({...filters, discount_card_name: null})
    setFiltersChanged(true)
  }

  const handleCardSourceChange = (cardSource) => {
    setFilters({...filters, card_source: cardSource})
    setFiltersChanged(true)
  }

  const isCustomDateRangeValid =
    (startDate == null || isValidDayjsValue(startDate)) &&
    (endDate == null || isValidDayjsValue(endDate)) &&
    (startDate == null || endDate == null || isValidCustomDateRange(startDate, endDate));

  const isUpdateDisabled = !isCustomDateRangeValid;

  const handleReset = () => {
    setFiltersChanged(false)
    setAppliedFilters({})
    setFilters({})
    setStartDate(null)
    setEndDate(null)
    setItemsPerPage(25);
    setPage(1);
  }

  const handleUpdate = () => {
    setFiltersChanged(false)
    setAppliedFilters(filters)
    setItemsPerPage(25);
    setPage(1);
  }

  const formattedFilters = (sourceFilters = filters) => {
    const params = {};
    if (sourceFilters?.restaurant_group_id) {
      params.restaurant_group_id = sourceFilters?.restaurant_group_id;
    }
    if(sourceFilters?.discount_card_name) {
      params.discount_card_name = sourceFilters?.discount_card_name
    }
    if(sourceFilters?.action_type) {
      params.action_type = sourceFilters?.action_type
    }
    if(sourceFilters?.performed_by_user_id) {
      params.performed_by_user_id = sourceFilters?.performed_by_user_id
    }
    if(sourceFilters?.from_date || sourceFilters?.to_date) {
      params.date_range = {
        from: sourceFilters?.from_date || '',
        to: sourceFilters?.to_date || '',
      }
    }
    Object.assign(params, cardSourceParams(sourceFilters?.card_source))

    return params;
  }

  const getAdminUserName = (id) => {
    const name = adminUserOptionsCache?.find((user) => user.value === id)
    return name?.label ? name?.label : ''
  }

  const getRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value  : ''
  }

  const getDiscountCardName = (value) => {
    const discountCard = discountCardOptionsCache?.find((card) => card?.value === value)
    return discountCard?.label || value || ''
  }

  const tableColumns = [
    { key: "discount_card", label: "Perq Card", sortable: true },
    { key: "performed_by", sortable: true },
    { key: "performed_at", sortable: true },
    { key: "action_type", sortable: true },
    { key: "modified_attributes", sortable: true },
    { key: "previous", sortable: false },
    { key: "new", sortable: false },
  ];

  const columns = tableColumns?.map((column) => {
    return {
      key: column.key,
      label: column.label || camelize(column.key),
      sortable: column.sortable,
    };
  });

  const rows = useMemo(() => {
    return (data?.logData || []).map((record) => {
      return {
        discount_card: record?.discount_card_name ? record?.discount_card_name : '-',
        performed_by: record?.performed_by,
        performed_at: formatDate(record?.performed_at, record?.time_zone_abbr),
        action_type: {
          display: (
            <ActionContainer action={record?.action_type}>
              {capitalize(record?.action_type)}
            </ActionContainer>
          ),
          sortValue: capitalize(record?.action_type)
        },
        modified_attributes: {
          display: (
            <LogAttributes data={record?.modified_attributes} />
          ),
          sortValue: record?.modified_attributes?.[0]
        },
        previous: record?.previous_value ? <LogValueGroups data={record?.previous_value} /> : '-',
        new: record?.new_value ? <LogValueGroups data={record?.new_value} /> : record?.action_type === 'update' ? 'Removed' : '-'
      };
    });
  }, [data?.logData]);

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      // Export what the table shows: the filters from the last Update.
      const query = buildQueryParams(formattedFilters(appliedFilters))
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=discount_card_logs&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `discount_card_logs_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  return (
    <DiscountLogPageContainer>
      {/* Bread Crumbs */}
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink 
            LinkComponent={StyledLink} 
            label="Home" to="/admin/dashboard" 
          />
          <StyledLink color={'#515152'}>
            Logs
          </StyledLink>
          <StyledLink color={'#515152'}>
            Perq Card Logs
          </StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      {/* Filter Section */}
      <FilterContainer>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={9}
            lg={9}
            container
            spacing={2}
            alignItems="center"
          >
            <Grid item>
              <Typography
                variant="body2"
                sx={{
                  ...viewTypographyStyles,
                  alignSelf: { xs: "flex-start", sm: "center" },
                  whiteSpace: "nowrap",
                  mt: 0
                }}
              >
                  View
              </Typography>
            </Grid>

            <Grid item>
              <FormControl size="small">
                <StyledInputLabel>Restaurant Group</StyledInputLabel>
                <Select
                  size="small"
                  label="Restaurant Group"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: 160,
                    width: 160,
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleRestaurantGroupChange}
                  onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                  value={hasSingleAvailableRestaurantGroup ? restaurantGroups?.[0]?.label : (filters?.restaurant_group_id || "")}
                  renderValue={() =>
                    hasSingleAvailableRestaurantGroup ? restaurantGroups?.[0]?.value : getRestaurantGroupName(filters?.restaurant_group_id)
                  }
                  endAdornment = {
                    filters?.restaurant_group_id ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleRestaurantGroupClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                  {restaurantGroups.map((res) => (
                    <StyledMenuItem
                      key={res?.label}
                      value={res?.label}
                    >
                      {
                        hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                      }
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantGroupsPage}
                    targetRef={restaurantGroupLoaderRef}
                  />
                </Select>
              </FormControl>
            </Grid>

            <Grid item>
              <FormControl size="small">
                <StyledInputLabel>Perq Card</StyledInputLabel>
                <Select
                  value={hasSingleAvailableDiscountCard ? discountCards?.[0]?.value : (filters?.discount_card_name || '')}
                  label='Perq Card'
                  size="small"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: discountCardMenuRef, sx: { maxHeight: "40vh" } },
                    autoFocus: false,
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: 120,
                    width: 140,
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleDiscountCardChange}
                  onClose={() => setTimeout(() => setDiscountCardSearch(''), 1000)}
                  renderValue={() =>
                    hasSingleAvailableDiscountCard ? discountCards?.[0]?.label : getDiscountCardName(filters?.discount_card_name)
                  }
                  endAdornment={
                    filters?.discount_card_name ? (
                      <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                        <IconButton
                          edge='end'
                          size="small"
                          disableRipple
                          onClick={handleDiscountCardClear}
                        >
                          <ClearIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                      </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setDiscountCardSearch} placeholder={'Search perq card'} isFetching={discountCardFetching} />
                  {discountCards.map((res, index) => (
                    <StyledMenuItem
                      key={index}
                      value={res?.value}
                    >
                      { hasSingleAvailableDiscountCard || filters?.discount_card_name === res?.value ? <SelectedMenuItem data={res?.label} /> : res?.label }
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextDiscountCardsPage}
                    targetRef={discountCardLoaderRef}
                  />
                </Select>
                </FormControl>
            </Grid>

            <Grid item>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  slotProps={{
                    textField: { placeholder: "Date From" },
                  }}
                  sx={datePickerStyles}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={endDate}
                  onChange={handleToDateChange}
                  slotProps={{
                    textField: { placeholder: "Date To" },
                  }}
                  sx={datePickerStyles}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item>
              <FormControl size="small">
                <StyledInputLabel>Action Type</StyledInputLabel>
                <Select
                  value={filters?.action_type || ''}
                  renderValue={() =>
                    capitalize(filters?.action_type)
                  }
                  size="small"
                  label="Action Type"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { sx: { width: '220px' } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: 120,
                    width: 130,
                    borderColor: "#2b4fb9",
                  }}
                  onChange={handleEventTypeChange}
                  endAdornment= {
                    filters?.action_type ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleActionTypeClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  {actionType?.map((event) => (
                    <StyledMenuItem
                      key={event?.value}
                      value={event?.value}
                    >
                      {
                        filters?.action_type === event?.value ? <SelectedMenuItem data={event?.label} /> : event?.label
                      }
                    </StyledMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item>
              <CardSourceFilter
                value={filters?.card_source}
                onChange={handleCardSourceChange}
                disabled={!filters?.restaurant_group_id}
              />
            </Grid>

            <Grid item>
              <FormControl size="small">
                <StyledInputLabel>Admin User</StyledInputLabel>
                <Select
                  value={filters?.performed_by_user_id || ''}
                  renderValue={() =>
                    getAdminUserName(filters?.performed_by_user_id)
                  }
                  size="small"
                  label="Admin User"
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: { ref: adminUserMenuRef, sx: { maxHeight: "40vh" } },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                  sx={{
                    ...selectBaseStyles,
                    minWidth: 120,
                    width: 130,
                    borderColor: "#2b4fb9",
                  }}
                  onClose={() => setTimeout(() => setAdminSearch(''), 1000)}
                  onChange={handleAdminUserChange}
                  endAdornment= {
                    filters?.performed_by_user_id ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleAdminUserClear}
                      >
                        <ClearIcon sx={{fontSize: '20px'}}/>
                      </IconButton>
                    </InputAdornment>
                    ) : (
                      null
                    )
                  }
                >
                  <DropdownSearchBar changeSearchText={setAdminSearch} placeholder={'Search admin user'} isFetching={adminUserFetching} />
                  {adminUsers?.map((user) => (
                    <StyledMenuItem
                      key={user?.value}
                      value={user?.value}
                    >
                      {
                        filters?.performed_by_user_id === user?.value ? <SelectedMenuItem data={user?.label} /> : user?.label
                      }
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextAdminUsersPage}
                    targetRef={adminUserLoaderRef}
                  />
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={3}
            lg={3}
            container
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            direction={{ xs: 'column', sm: 'column', md: 'row' }}
            alignItems="center"
            spacing={1}
          >
            <Grid item>
              <ResetButton
                onClick={handleReset}
                filtersChanged={filtersChanged}
                status={isFetching}
              />
            </Grid>

            <Grid item>
              <UpdateButton
                onClick={handleUpdate}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Grid>
          </Grid>
        </Grid>
      </FilterContainer>

      <MenuHeading report={false}>Perq Card Logs</MenuHeading>
      {
        isFetching ? (
          <Loader />
        ) : (
          <IndexTable
            columns={columns}
            data={rows}
            tableId="discount-card-logs"
            type="entries"
            sortMode="server"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={data?.pageCount}
            report={false}
            showDownloadButton={true}
            disableDownloadButton={data?.logData?.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
          />
        )
      }
    </DiscountLogPageContainer>
  )
}
